import { defineStore } from 'pinia';
import { ref } from 'vue';
import * as apiService from '@/services/apiService.js';
import { useAuthStore } from '@/stores/userStore';

export const useFeedStore = defineStore('feed', () => {
    const treeGroups = ref([]);
    const rssNodes = ref([]);
    const isLoading = ref(false);
    const selectedNode = ref(null);

    const setLoading = (val) => {
        isLoading.value = val;
    };

    const fetchData = async () => {
        try {
            const authStore = useAuthStore();
            const token = authStore.isAuthenticated ? authStore.token : undefined;
            setLoading(true);

            const [groups, nodes] = await Promise.all([
                apiService.fetchFeedGroups(() => { }, token),
                apiService.fetchRssNodes(() => { }, token)
            ]);

            treeGroups.value = groups || [];
            rssNodes.value = nodes || [];
            // Auto-expand first group
            if (treeGroups.value.length > 0) {
                treeGroups.value[0].isOpen = true;
            }

            // Auto-select node based on isFirst flag
            if (rssNodes.value.length > 0) {
                const defaultNode = rssNodes.value.find(n => n.isFirst === 1) || rssNodes.value[0];
                selectedNode.value = defaultNode;
                await fetchArticles(defaultNode.id, 1);
            }

        } catch (error) {
            console.error("Error fetching feed data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Article State
    const articles = ref([]);
    const totalArticles = ref(0);
    const currentPage = ref(1);
    const articlesPerPage = ref(100);

    // Actions
    const fetchArticles = async (nodeId, page = 1) => {
        try {
            setLoading(true);
            const response = await apiService.fetchFeedsBySite(
                nodeId,
                page,
                articlesPerPage.value,
                () => { }
            );

            const sortedItems = (response.items || []).sort((a, b) => {
                const dateCompare = new Date(b.pubDate) - new Date(a.pubDate);
                if (dateCompare !== 0) return dateCompare;
                return b.id - a.id;
            });

            articles.value = sortedItems;
            totalArticles.value = response.totalItems;
            currentPage.value = response.pageIndex;

            const node = rssNodes.value.find(n => n.id === nodeId);
            if (node) {
                node.totalFeed = response.totalItems;
            }

        } catch (error) {
            console.error("Error fetching articles:", error);
            articles.value = [];
        } finally {
            setLoading(false);
        }
    };

    const getNodesForGroup = (groupId) => {
        return rssNodes.value.filter(node => node.groupId === groupId);
    };

    return {
        treeGroups,
        rssNodes,
        isLoading,
        selectedNode,
        articles,
        totalArticles,
        currentPage,
        articlesPerPage,
        fetchData,
        fetchArticles,
        setLoading,
        getNodesForGroup
    };
});
